// src/hooks/useRoles.js - Updated with SuperContentManager role
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApiCall } from './useApiCall';
import apiService from '../services/apiService';

// Module-level cache, shared across every useRoles() instance in the app.
// This hook is called independently by 10+ components (ReportingDashboard,
// AdminSection, UserManagement, the RPT-001/002/003 pages, etc.), each of
// which used to fetch /api/v1/users/profile on its own mount with no
// sharing — routes that unmount/remount on navigation (like the RPT
// drilldown) multiplied that fetch on every hop, contributing to hitting the
// API rate limit during normal use. Cache by uid; a fresh fetch only happens
// once per signed-in user per page load, and concurrent mounts share one
// in-flight request instead of firing their own.
let cachedProfile = null;
let cachedForUid = null;
let inFlightRequest = null;

export const useRoles = () => {
  const { user } = useAuth();
  const [userProfile, setUserProfile] = useState(
    user && cachedForUid === user.uid ? cachedProfile : null
  );
  const { loading, error, makeRequest } = useApiCall();

  useEffect(() => {
    if (!user) return;

    if (cachedForUid === user.uid && cachedProfile) {
      setUserProfile(cachedProfile);
      return;
    }

    let cancelled = false;

    (async () => {
      if (!inFlightRequest) {
        inFlightRequest = makeRequest(
          () => apiService.get('/api/v1/users/profile', user),
          'Loading your profile...'
        ).finally(() => { inFlightRequest = null; });
      }
      try {
        const data = await inFlightRequest;
        cachedProfile = data.data;
        cachedForUid = user.uid;
        if (!cancelled) setUserProfile(data.data);
      } catch (err) {
        console.error('Error loading user profile:', err);
      }
    })();

    return () => { cancelled = true; };
  }, [user]);

  const loadUserProfile = async () => {
    try {
      const data = await makeRequest(
        () => apiService.get('/api/v1/users/profile', user),
        'Loading your profile...'
      );

      cachedProfile = data.data;
      cachedForUid = user?.uid ?? cachedForUid;
      setUserProfile(data.data);
      return data.data;
    } catch (err) {
      console.error('Error loading user profile:', err);
      throw err;
    }
  };

  // Helper functions to check roles
  const hasRole = (role) => {
    return userProfile && userProfile.roles && userProfile.roles.includes(role);
  };

  const hasAnyRole = (roles) => {
    return userProfile && userProfile.roles && roles.some(role => userProfile.roles.includes(role));
  };

  const hasAdminAccess = () => {
    if (!userProfile || !userProfile.roles) return false;
    
    const roles = userProfile.roles;
    return roles.length > 1 || (roles.length === 1 && roles[0] !== 'User');
  };

  const isSuperAdmin = () => hasRole('SuperAdmin') || hasRole('superAdmin'); // Handle both cases
  const isAdmin = () => hasRole('Admin');
  const isContentManager = () => hasRole('ContentManager');
  const isSuperContentManager = () => hasRole('SuperContentManager'); // New role
  const isCoach = () => hasRole('Coach');
  const isUser = () => hasRole('User');

  const getRoleBadge = () => {
    if (!userProfile || !userProfile.roles) return null;
    
    const roles = userProfile.roles;
    if (roles.includes('SuperAdmin') || roles.includes('superAdmin')) {
      return { text: 'Super Admin', class: 'bg-purple-100 text-purple-600' };
    }
    if (roles.includes('SuperContentManager')) {
      return { text: 'Super Content Manager', class: 'bg-green-100 text-green-700' };
    }
    if (roles.includes('Admin')) {
      return { text: 'Admin', class: 'bg-blue-100 text-blue-700' };
    }
    if (roles.includes('ContentManager')) {
      return { text: 'Content Manager', class: 'bg-orange-100 text-orange-600' };
    }
    if (roles.includes('Coach')) {
      return { text: 'Coach', class: 'bg-teal-100 text-teal-700' };
    }
    return null;
  };

  const getHighestRole = () => {
    if (!userProfile || !userProfile.roles) return 'User';

    const roles = userProfile.roles;
    if (roles.includes('SuperAdmin') || roles.includes('superAdmin')) return 'SuperAdmin';
    if (roles.includes('SuperContentManager')) return 'SuperContentManager';
    if (roles.includes('Admin')) return 'Admin';
    if (roles.includes('ContentManager')) return 'ContentManager';
    if (roles.includes('Coach')) return 'Coach';
    return 'User';
  };

  return {
    userProfile,
    loading,
    error,
    loadUserProfile,
    hasRole,
    hasAnyRole,
    hasAdminAccess,
    isSuperAdmin,
    isAdmin,
    isContentManager,
    isSuperContentManager, // New role function
    isCoach,
    isUser,
    getRoleBadge,
    getHighestRole
  };
};